import React from "react";
import {
  Calendar,
  Dumbbell,
  Eye,
  EyeOff,
  Mail,
  Lock,
  Users,
} from "lucide-react";

export function TennisBallIcon({ size = 20, className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M4.2 7.2 C 7.6 10.2, 7.6 13.8, 4.2 16.8" />
      <path d="M19.8 7.2 C 16.4 10.2, 16.4 13.8, 19.8 16.8" />
    </svg>
  );
}

export function GoogleIcon({ size = 20 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

export function AuthTextField({
  id,
  name,
  label,
  type = "text",
  value,
  onChange,
  onInput,
  disabled = false,
  autoComplete,
  placeholder,
  leadingIcon: LeadingIcon,
  trailing,
  inputRef,
}) {
  return (
    <label className="authPage__field" htmlFor={id}>
      <span className="authPage__fieldLabel">{label}</span>
      <div
        className={`authPage__inputWrap${trailing ? " authPage__inputWrap--hasTrailing" : ""}`}
      >
        {LeadingIcon ? (
          <span className="authPage__inputIcon" aria-hidden="true">
            <LeadingIcon size={18} strokeWidth={1.75} />
          </span>
        ) : null}
        <input
          id={id}
          name={name}
          className="authPage__input"
          type={type}
          value={value}
          ref={inputRef}
          disabled={disabled}
          autoComplete={autoComplete}
          placeholder={placeholder}
          onChange={onChange}
          onInput={onInput}
        />
        {trailing ? (
          <span className="authPage__inputTrailing">{trailing}</span>
        ) : null}
      </div>
    </label>
  );
}

export function AuthPasswordToggle({
  visible,
  onToggle,
  labelShow,
  labelHide,
}) {
  return (
    <button
      type="button"
      className="authPage__passwordToggle"
      onMouseDown={(event) => event.preventDefault()}
      onClick={onToggle}
      aria-label={visible ? labelHide : labelShow}
    >
      {visible ? (
        <EyeOff size={18} strokeWidth={1.75} />
      ) : (
        <Eye size={18} strokeWidth={1.75} />
      )}
    </button>
  );
}

const AUTH_FEATURES = Object.freeze([
  { key: "teams", Icon: Users },
  { key: "training", Icon: Dumbbell },
  { key: "tournaments", Icon: Calendar },
]);

export function AuthBrandPanel({ t }) {
  return (
    <aside
      className="authPage__brand"
      aria-label={t("auth.page.brandPanelAria")}
    >
      <div className="authPage__brandInner">
        <h1 className="authPage__headline">
          <span className="authPage__headlineLine">
            {t("auth.page.headlineLine1")}
          </span>
          <span className="authPage__headlineLine">
            {t("auth.page.headlineLine2")}
          </span>
          <span className="authPage__headlineLine authPage__headlineLine--accent">
            {t("auth.page.headlineLine3")}
          </span>
        </h1>
        <p className="authPage__tagline">{t("auth.page.tagline")}</p>
        <ul className="authPage__features">
          {AUTH_FEATURES.map(({ key, Icon }) => (
            <li key={key} className="authPage__feature">
              <span className="authPage__featureIcon" aria-hidden="true">
                <Icon size={20} strokeWidth={1.75} />
              </span>
              <span className="authPage__featureText">
                <strong className="authPage__featureTitle">
                  {t(`auth.page.feature.${key}.title`)}
                </strong>
                <span className="authPage__featureDesc">
                  {t(`auth.page.feature.${key}.desc`)}
                </span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}

export function AuthMobileBrand({ t }) {
  return (
    <div className="authPage__mobileBrand">
      <AuthPageLogo t={t} />
      <h1 className="authPage__mobileHeadline">
        {t("auth.page.mobileHeadline")}
      </h1>
      <p className="authPage__mobileTagline">{t("auth.page.mobileTagline")}</p>
    </div>
  );
}

export function AuthPageLogo({ t }) {
  return (
    <div className="authPage__logo">
      <span className="authPage__logoBadge" aria-hidden="true">
        <TennisBallIcon size={18} />
      </span>
      <span className="authPage__logoText">{t("auth.page.brandMark")}</span>
    </div>
  );
}
